import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Video,
  Send,
  Clock,
  CheckCircle2,
  PlayCircle,
  AlertCircle,
  Loader2,
  Star,
  FileText,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAppDispatch } from "../../app/hooks";
import { assignInterviewApi } from "../../features/ApplicationData/ApplicationDataSlice";
import {
  fetchInterviewQuestion,
  fetchInterviewQuestionDetail,
} from "../../features/jobApplication/JobApplicationSlice";

// --- Types ---
interface Question {
  id: string;
  text: string;
}

interface InterviewTemplate {
  id: string;
  title: string;
  questions: Question[];
}

interface CandidateAnswer {
  questionId: string;
  questionText: string;
  videoUrl: string;
  duration: number; // in seconds
}

interface InterviewStatus {
  isAssigned: boolean;
  isCompleted: boolean;
  isStarted?: boolean;
  assignedAt?: string;
  completedAt?: string;
  startedAt?: string;
  answers?: CandidateAnswer[];
}

interface InterviewQuestionDetail {
  id: string;
  question_no: number;
  question: string;
  question_detail?: string;
  question_type?: string;
  time_limit?: number;
  status: string;
  duration?: number | null;
  answer_text?: string | null;
  video_url?: string | null;
  audio_url?: string | null;
  transcript?: string | null;
  ai_summary?: string | null;
  ai_score?: number | null;
  started_at?: string | null;
  submitted_at?: string | null;
}

interface InterviewDetail {
  id: string;
  base_id: number;
  app_id: string;
  job_id: string;
  candidate_id: string;
  total_questions: number;
  completed_questions: number;
  status: string;
  assigned_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  questions: InterviewQuestionDetail[];
  final_ai_summary?: string;
}

export default function CandidateAiInterview() {
  const { baseId, candidateId, jobId, applicationId } = useParams<{
    baseId: any;
    candidateId: string;
    jobId: any;
    applicationId: any;
  }>();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [interviewStatus, setInterviewStatus] = useState<InterviewStatus>({
    isAssigned: false,
    isCompleted: false,
    isStarted: false,
  });
  const [interviewDetail, setInterviewDetail] =
    useState<InterviewDetail | null>(null);
  const [videoModalUrl, setVideoModalUrl] = useState<string | null>(null);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  // Fetch candidate's current interview status
  useEffect(() => {
    const fetchQuestion = async () => {
      setLoading(true);
      try {
        const res: any = await dispatch(
          fetchInterviewQuestion({
            baseId: parseInt(baseId),
            id: applicationId,
          }),
        ).unwrap();
        console.log("res========>", res);

        if (res.data?.length > 0) {
          const item = res.data[0];
          setInterviewStatus({
            isAssigned:
              item.status === "assigned" || item.status === "completed",
            isCompleted: item.status === "completed",
            isStarted: item.status === "started",
            assignedAt: item.assigned_at,
            completedAt: item.completed_at,
            startedAt: item.started_at,
          });
          if (item.status === "completed") {
            fetchQuestionDetail(item.id);
          }
        } else {
          setInterviewStatus({
            isAssigned: false,
            isCompleted: false,
            isStarted: false,
          });
        }
      } catch (error) {
        toast.error("Failed to load interview status");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, baseId, jobId]);

  const fetchQuestionDetail = async (id: any) => {
    setLoading(true);
    try {
      const res: any = await dispatch(
        fetchInterviewQuestionDetail({
          baseId: parseInt(baseId),
          interviewId: id,
        }),
      ).unwrap();
      console.log("detail res========>", res);
      if (res.data) {
        setInterviewDetail(res.data);
      }
    } catch (error) {
      toast.error("Failed to load interview details");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignInterview = async () => {
    // if (!selectedTemplate) {
    //   toast.error("Please select an interview template.");
    //   return;
    // }

    try {
      setAssigning(true);
      // TODO: Replace with your actual API endpoint
      // await axios.post(`/api/admin/candidates/${candidateId}/assign-interview`, { templateId: selectedTemplate });
      const payload = {
        app_id: applicationId,
        job_id: jobId,
        question_count: 10,
      };
      console.log("payload", payload);

      //   // Call the API
      const res = await dispatch(
        assignInterviewApi({
          baseId: baseId,
          data: payload,
        }),
      );
      console.log("res====>", res);
      toast.success(
        "Interview assigned successfully! The candidate has been notified.",
      );

      setInterviewStatus({
        isAssigned: true,
        isCompleted: false,
        isStarted: false,
        assignedAt: new Date().toISOString(),
      });
    } catch (error) {
      toast.error("Failed to assign interview");
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center relative z-50 mt-40">
        <div className="simple-spinner-loader">
          <span className="!w-6 !h-6"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6">
      {/* <div className="flex items-center gap-3 mb-8 border-b pb-4">
        <div className="p-3 bg-indigo-100 rounded-lg">
          <Video className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Video Interview</h1>
          <p className="text-sm text-gray-500">Manage and review automated video screening</p>
        </div>
      </div> */}

      {/* STATE 1: Not Assigned */}
      {!interviewStatus.isAssigned && !interviewStatus.isStarted && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex flex-col items-center gap-4 mb-6 w-full">
            <div className="mx-auto w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>

            <div className="flex flex-col item-center w-full">
              <h3 className="text-base font-semibold text-gray-900 text-center">
                No Interview Assigned
              </h3>
              <p className="text-gray-500 text-sm mt-1 text-center">
                This candidate has not been assigned a video interview yet.
                Select a template below to send them an invitation.
              </p>
            </div>
          </div>

          <div className="space-y-4  w-full">
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Interview Template
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600 outline-none transition-all"
              >
                <option value="">-- Select a template --</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.title} ({tpl.questions.length} questions)
                  </option>
                ))}
              </select>
            </div> */}

            <button
              onClick={handleAssignInterview}
              disabled={assigning}
              //  max-w-md
              className="flex items-center justify-center gap-2 w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {assigning ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {assigning ? "Assigning..." : "Assign & Notify Candidate"}
            </button>
          </div>
        </div>
      )}

      {/* Assigned, but Pending */}
      {interviewStatus.isAssigned && !interviewStatus.isCompleted && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-blue-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Pending Submission
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            The interview invitation was sent. We are currently waiting for the
            candidate to record and submit their video responses.
          </p>
          {interviewStatus.assignedAt && (
            <p className="text-xs text-gray-400 mt-6">
              Assigned on:{" "}
              {new Date(interviewStatus.assignedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* STATE 2.5: Started (Candidate has started the interview but not completed) */}
      {interviewStatus.isStarted && !interviewStatus.isCompleted && (
        <div className="bg-white border border-gray-200 rounded-xl p-8  text-center">
          <div className="mx-auto w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4">
            <Video className="w-8 h-8 text-purple-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Interview In Progress
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            The candidate has started the video interview. Responses will be
            available here once the interview is submitted.
          </p>
          {interviewStatus.startedAt && (
            <p className="text-xs text-gray-400 mt-6">
              Started on:{" "}
              {new Date(interviewStatus.startedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* STATE 3: Completed (Show Summary + Videos) */}
      {interviewStatus.isAssigned && interviewStatus.isCompleted && (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-900">
                  Interview Completed
                </h3>
                <p className="text-sm text-green-700">
                  The candidate has submitted all required video responses.
                </p>
              </div>
            </div>
            {interviewDetail?.completed_at && (
              <span className="text-sm font-medium text-green-800 bg-green-100 px-3 py-1 rounded-full">
                {new Date(interviewDetail.completed_at).toLocaleDateString(
                  "en-GB",
                )}
              </span>
            )}
          </div>

          {/* Summary */}
          {interviewDetail && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">
                    Total Questions
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {interviewDetail.total_questions}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">
                    Completed
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {interviewDetail.completed_questions}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">
                    Assigned
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {interviewDetail.assigned_at
                      ? new Date(
                          interviewDetail.assigned_at,
                        ).toLocaleDateString("en-GB")
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">
                    Avg Score
                  </p>
                  <p className="text-lg font-semibold text-indigo-600">
                    {(() => {
                      const scored =
                        interviewDetail.questions?.filter(
                          (q) => q.ai_score != null,
                        ) || [];
                      if (scored.length === 0) return "-";
                      const avg =
                        scored.reduce(
                          (s, q) => s + Number(q.ai_score || 0),
                          0,
                        ) / scored.length;
                      return avg.toFixed(1);
                    })()}
                  </p>
                </div>
              </div>
              {interviewDetail?.final_ai_summary && (
                <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg">
                  <h5 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" />
                    AI Final Summary
                  </h5>
                  <p
                    className={`text-sm text-slate-700 ${
                      isSummaryExpanded ? "" : "line-clamp-2"
                    }`}
                  >
                    {interviewDetail?.final_ai_summary}
                  </p>
                  {interviewDetail.final_ai_summary.length > 120 && (
                    <button
                      type="button"
                      onClick={() => setIsSummaryExpanded((prev) => !prev)}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 mt-1"
                    >
                      {isSummaryExpanded ? "Read Less" : "Read More"}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Video Responses List */}
          <div className="space-y-8 mt-2">
            {interviewDetail?.questions?.map((q, index) => {
              const durationSec = q.duration
                ? q.duration
                : q.started_at && q.submitted_at
                  ? Math.round(
                      (new Date(q.submitted_at).getTime() -
                        new Date(q.started_at).getTime()) /
                        1000,
                    )
                  : null;
              return (
                <div
                  key={q.id}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                >
                  <div className="bg-gray-50 border-b border-gray-200 p-4">
                    <div className="flex gap-3">
                      <span className="flex items-center justify-center bg-indigo-100 text-indigo-700 font-bold w-6 h-6 rounded-full text-xs shrink-0">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <h4 className="text-gray-900 font-medium leading-relaxed">
                          {q.question_detail}
                        </h4>
                        {q.question && (
                          <p className="text-xs text-gray-500 mt-1">
                            {q.question}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${
                            q.status === "processed"
                              ? "bg-green-100 text-green-700"
                              : q.status === "submitted"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {q.status}
                        </span> */}
                        {/* {q.ai_score != null && (
                          <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                            <Star className="w-3 h-3 fill-amber-500 stroke-amber-500" />
                            {Number(q.ai_score).toFixed(1)}
                          </span>
                        )} */}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 flex flex-col md:flex-row gap-6">
                    {/* Video */}
                    {/* {q.video_url ? (
                      <button
                        onClick={() => setVideoModalUrl(q.video_url ?? null)}
                        className="relative w-full max-w-md bg-black rounded-lg overflow-hidden aspect-video shrink-0 group cursor-pointer"
                      >
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 group-hover:bg-black/25 transition-colors">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <PlayCircle className="w-7 h-7 text-indigo-600 ml-0.5" />
                          </div>
                          <span className="text-white text-sm font-medium mt-2">
                            View Video
                          </span>
                        </div>
                      </button>
                    ) : (
                      <div className="w-full max-w-md bg-gray-50 border border-dashed border-gray-300 rounded-lg aspect-video flex flex-col items-center justify-center shrink-0">
                        <PlayCircle className="w-8 h-8 text-gray-300" />
                        <p className="text-xs text-gray-400 mt-1">
                          No response recorded
                        </p>
                      </div>
                    )} */}

                    {/* AI Analysis */}
                    <div className="flex-1 flex flex-col justify-center gap-3">
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 w-full justify-between">
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1.5">
                            <PlayCircle className="w-4 h-4" />
                            {durationSec != null
                              ? `Duration: ${durationSec}s`
                              : "Duration: -"}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            {q.time_limit ? `Limit: ${q.time_limit}s` : ""}
                          </span>
                          {q.ai_score != null &&
                            (() => {
                              const score = Number(q.ai_score);
                              let badgeClass = "";
                              let starClass = "";
                              if (score >= 7) {
                                badgeClass =
                                  "bg-green-50 text-green-700 border-green-100";
                                starClass = "fill-green-600 stroke-green-600";
                              } else if (score >= 4) {
                                badgeClass =
                                  "bg-amber-50 text-amber-600 border-amber-100";
                                starClass = "fill-amber-500 stroke-amber-500";
                              } else if (score >= 3) {
                                badgeClass =
                                  "bg-blue-50 text-blue-600 border-blue-100";
                                starClass = "fill-blue-500 stroke-blue-500";
                              } else {
                                badgeClass =
                                  "bg-red-50 text-red-600 border-red-100";
                                starClass = "fill-red-500 stroke-red-500";
                              }
                              return (
                                <span
                                  className={`flex items-center gap-2 text-xs font-medium border px-2 py-1 rounded-md ${badgeClass}`}
                                >
                                  <Star className={`w-3 h-3 ${starClass}`} />
                                  {`Score: ${score.toFixed(1)}`}
                                </span>
                              );
                            })()}
                        </div>
                        {q.video_url ? (
                          <button
                            onClick={() =>
                              setVideoModalUrl(q.video_url ?? null)
                            }
                            className="flex items-center justify-center gap-2 w-[150px] h-7 border border-indigo-600  bg-white hover:bg-indigo-100 text-indigo-600 font-medium py-2.5 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {/* <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 group-hover:bg-black/25 transition-colors">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <PlayCircle className="w-7 h-7 text-indigo-600 ml-0.5" />
                          </div> */}
                            <PlayCircle className="w-4 h-4" />
                            View Video
                            {/* </div> */}
                          </button>
                        ) : (
                          ""
                          // <div className="w-full max-w-md bg-gray-50 border border-dashed border-gray-300 rounded-lg aspect-video flex flex-col items-center justify-center shrink-0">
                          //   <PlayCircle className="w-8 h-8 text-gray-300" />
                          //   <p className="text-xs text-gray-400 mt-1">
                          //     No response recorded
                          //   </p>
                          // </div>
                        )}
                      </div>

                      {q.ai_summary && (
                        <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg">
                          <h5 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <Star className="w-3.5 h-3.5" />
                            AI Summary
                          </h5>
                          <p className="text-sm text-slate-700">
                            {q.ai_summary}
                          </p>
                        </div>
                      )}

                      {q.transcript && (
                        <div className="bg-slate-50 border border-slate-100 p-4 rounded-lg">
                          <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" />
                            Transcript
                          </h5>
                          <p className="text-sm text-slate-600 italic">
                            "{q.transcript}"
                          </p>
                        </div>
                      )}

                      {!q.ai_summary && !q.transcript && (
                        <div className="bg-slate-50 border border-slate-100 p-4 rounded-lg">
                          <p className="text-sm text-slate-500 italic">
                            No AI analysis available for this response yet.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Video Modal */}
      {videoModalUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setVideoModalUrl(null)}
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Video className="w-5 h-5 text-indigo-600" />
                Video Response
              </h3>
              <button
                onClick={() => setVideoModalUrl(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="w-full bg-black rounded-lg overflow-hidden aspect-video">
                <video
                  src={videoModalUrl}
                  controls
                  controlsList="nodownload"
                  autoPlay
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
